import React, { useState, useEffect } from 'react';
import { FileVideo, Search, Trash2, Eye, CheckCircle, XCircle, FolderOpen, RefreshCw, Shield, Bug, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Card, CardContent } from '../ui/card';
import { Badge } from '../ui/badge';
import { useToast } from '../../hooks/use-toast';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Checkbox } from '../ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import api from '../../services/api';

const ContentManagement = () => {
  const { toast } = useToast();
  const [content, setContent] = useState([]);
  const [pendingContent, setPendingContent] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('all');
  
  // Re-moderation state
  const [selectedContent, setSelectedContent] = useState(new Set());
  const [remodDialogOpen, setRemodDialogOpen] = useState(false);
  const [remodOptions, setRemodOptions] = useState({ run_ai_analysis: true, run_virus_scan: true });
  const [remodTarget, setRemodTarget] = useState(null); // null for bulk, or content_id for single
  const [remodLoading, setRemodLoading] = useState(false);
  const [remodResults, setRemodResults] = useState(null);

  useEffect(() => {
    fetchContent();
    fetchPendingContent();
  }, []);

  const fetchContent = async () => {
    try {
      const response = await api.get(`/content?limit=100`);
      setContent(response.data || []);
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to fetch content',
      });
    } finally {
      setLoading(false);
    }
  };

  const fetchPendingContent = async () => {
    try {
      const response = await api.get(`/admin/content/pending?limit=100`);
      setPendingContent(response.data || []);
    } catch (error) {
      console.error('Error fetching pending content:', error);
    }
  };

  const handleDelete = async (contentId, title) => {
    if (!window.confirm(`Are you sure you want to delete "${title}"?`)) return;

    try {
      await api.delete(`/admin/content/${contentId}`);
      
      toast({
        title: 'Content deleted',
        description: `"${title}" has been removed`,
      });
      
      fetchContent();
      fetchPendingContent();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to delete content',
      });
    }
  };

  const handleApprove = async (contentId, title) => {
    try {
      await api.post(`/admin/content/${contentId}/approve`, {});
      
      toast({
        title: 'Content approved',
        description: `"${title}" has been approved`,
      });
      
      fetchContent();
      fetchPendingContent();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to approve content',
      });
    }
  };

  const handleReject = async (contentId, title) => {
    const reason = window.prompt(`Reason for rejecting "${title}"?`);
    if (!reason) return;

    try {
      const formData = new URLSearchParams();
      formData.append('reason', reason);

      await api.post(
        `/admin/content/${contentId}/reject`,
        formData,
        { headers: { 'Content-Type': 'application/x-www-form-urlencoded' } }
      );
      
      toast({
        title: 'Content rejected',
        description: `"${title}" has been rejected`,
      });
      
      fetchContent();
      fetchPendingContent();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: 'Failed to reject content',
      });
    }
  };

  // Re-moderation handlers
  const openRemodDialog = (contentId = null) => {
    setRemodTarget(contentId);
    setRemodOptions({ run_ai_analysis: true, run_virus_scan: true });
    setRemodResults(null);
    setRemodDialogOpen(true);
  };

  const handleRemoderate = async () => {
    setRemodLoading(true);
    setRemodResults(null);
    
    try {
      if (remodTarget) {
        // Single content re-moderation
        const response = await api.post(
          `/moderation/remoderate/${remodTarget}`,
          remodOptions
        );
        
        const result = response.data;
        setRemodResults(result);
        
        if (result.action_taken === 'flagged_for_review') {
          toast({
            variant: 'destructive',
            title: 'Content Flagged',
            description: `"${result.content_title}" flagged: ${result.flagged_categories.join(', ')}`,
          });
        } else {
          toast({
            title: 'Re-moderation Complete',
            description: `"${result.content_title}" passed all checks`,
          });
        }
      } else {
        // Bulk re-moderation
        const contentIds = Array.from(selectedContent);
        if (contentIds.length === 0) {
          toast({
            variant: 'destructive',
            title: 'No Content Selected',
            description: 'Please select content items to re-moderate',
          });
          setRemodLoading(false);
          return;
        }
        
        const response = await api.post(
          `/moderation/remoderate/bulk`,
          { content_ids: contentIds, options: remodOptions }
        );
        
        const result = response.data;
        setRemodResults(result);
        
        toast({
          title: 'Bulk Re-moderation Complete',
          description: `Processed: ${result.processed}, Passed: ${result.passed}, Flagged: ${result.flagged}`,
        });
        
        setSelectedContent(new Set());
      }
      
      fetchContent();
      fetchPendingContent();
    } catch (error) {
      toast({
        variant: 'destructive',
        title: 'Re-moderation Failed',
        description: error.response?.data?.detail || 'Failed to re-moderate content',
      });
    } finally {
      setRemodLoading(false);
    }
  };

  const toggleContentSelection = (contentId) => {
    setSelectedContent(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contentId)) {
        newSet.delete(contentId);
      } else {
        newSet.add(contentId);
      }
      return newSet;
    });
  };

  const selectAllApproved = () => {
    const approvedIds = content
      .filter(item => item.status === 'approved')
      .map(item => item.id);
    setSelectedContent(new Set(approvedIds));
  };

  const clearSelection = () => {
    setSelectedContent(new Set());
  };

  const filteredContent = content.filter(item =>
    item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    item.description?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getTypeBadge = (type) => {
    const colors = {
      video: 'bg-red-500',
      audio: 'bg-purple-500',
      image: 'bg-blue-500'
    };
    return colors[type] || 'bg-gray-500';
  };

  const renderContentGrid = (contentList, showApprovalButtons = false) => {
    if (contentList.length === 0) {
      return (
        <div className="text-center py-12 md:py-20">
          <FileVideo className="w-12 h-12 md:w-16 md:h-16 mx-auto mb-4 text-gray-600" />
          <p className="text-gray-400">No content found</p>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
        {contentList.map((item) => {
          const isApproved = item.status === 'approved';
          const isSelected = selectedContent.has(item.id);
          
          return (
            <Card key={item.id} className={`bg-gray-800/50 border-gray-700 ${isSelected ? 'ring-2 ring-purple-500' : ''}`}>
              <CardContent className="p-3 md:p-4">
                {/* Selection checkbox for approved content */}
                {isApproved && !showApprovalButtons && (
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id={`select-${item.id}`}
                        checked={isSelected}
                        onCheckedChange={() => toggleContentSelection(item.id)}
                        className="border-gray-600"
                        data-testid={`select-content-${item.id}`}
                      />
                      <label 
                        htmlFor={`select-${item.id}`} 
                        className="text-xs text-gray-400 cursor-pointer"
                      >
                        Select
                      </label>
                    </div>
                    <Badge className={`${item.status === 'approved' ? 'bg-green-600' : item.status === 'flagged' ? 'bg-yellow-600' : 'bg-gray-600'} text-white text-xs`}>
                      {item.status}
                    </Badge>
                  </div>
                )}
                
                {item.thumbnail_url && (
                  <img
                    src={item.thumbnail_url}
                    alt={item.title}
                    className="w-full h-32 md:h-40 object-cover rounded-lg mb-3"
                  />
                )}
                <div className="flex items-start justify-between mb-2">
                  <h3 className="font-semibold text-white text-sm md:text-lg flex-1 line-clamp-2">
                    {item.title}
                  </h3>
                  <Badge className={`${getTypeBadge(item.content_type)} text-white ml-2 text-xs`}>
                    {item.content_type}
                  </Badge>
                </div>
                <p className="text-xs md:text-sm text-gray-400 line-clamp-2 mb-2 md:mb-3">
                  {item.description || 'No description'}
                </p>
                <div className="flex items-center justify-between text-xs text-gray-500 mb-2 md:mb-3">
                  <span>{item.view_count || 0} views</span>
                  <span>{new Date(item.created_at).toLocaleDateString()}</span>
                </div>
                <div className="flex gap-2 flex-wrap">
                  {showApprovalButtons && (
                    <>
                      <Button
                        onClick={() => handleApprove(item.id, item.title)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-green-600 text-green-500 hover:bg-green-900/20 text-xs"
                        data-testid={`approve-content-${item.id}`}
                      >
                        <CheckCircle className="w-3 h-3 mr-1" />
                        Approve
                      </Button>
                      <Button
                        onClick={() => handleReject(item.id, item.title)}
                        variant="outline"
                        size="sm"
                        className="flex-1 border-yellow-600 text-yellow-500 hover:bg-yellow-900/20 text-xs"
                        data-testid={`reject-content-${item.id}`}
                      >
                        <XCircle className="w-3 h-3 mr-1" />
                        Reject
                      </Button>
                    </>
                  )}
                  
                  {/* Re-moderate button for approved content only */}
                  {isApproved && !showApprovalButtons && (
                    <Button
                      onClick={() => openRemodDialog(item.id)}
                      variant="outline"
                      size="sm"
                      className="flex-1 border-purple-600 text-purple-400 hover:bg-purple-900/20 text-xs"
                      data-testid={`remoderate-content-${item.id}`}
                    >
                      <RefreshCw className="w-3 h-3 mr-1" />
                      Re-moderate
                    </Button>
                  )}
                  
                  <Button
                    onClick={() => handleDelete(item.id, item.title)}
                    variant="destructive"
                    size="sm"
                    className={`text-xs ${showApprovalButtons || isApproved ? '' : 'flex-1'}`}
                    data-testid={`delete-content-${item.id}`}
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Delete
                  </Button>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>
    );
  };

  return (
    <div className="w-full">
      <div className="mb-4 md:mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h2 className="text-xl md:text-3xl font-bold text-white mb-1 md:mb-2">Content Management</h2>
          <p className="text-sm text-gray-400">Manage all uploaded content</p>
        </div>
        <div className="relative w-full sm:w-64 md:w-96">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
          <Input
            type="text"
            placeholder="Search content..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 bg-gray-800 border-gray-600 text-white text-sm"
            data-testid="content-search-input"
          />
        </div>
      </div>

      {/* Bulk Re-moderation Controls */}
      {activeTab === 'all' && selectedContent.size > 0 && (
        <div className="mb-4 p-3 bg-purple-900/30 border border-purple-700 rounded-lg flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Shield className="w-5 h-5 text-purple-400" />
            <span className="text-purple-200 text-sm">
              <strong>{selectedContent.size}</strong> approved content item{selectedContent.size !== 1 ? 's' : ''} selected
            </span>
          </div>
          <div className="flex gap-2">
            <Button
              onClick={clearSelection}
              variant="outline"
              size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
              data-testid="clear-selection-btn"
            >
              Clear Selection
            </Button>
            <Button
              onClick={() => openRemodDialog(null)}
              variant="outline"
              size="sm"
              className="border-purple-600 text-purple-400 hover:bg-purple-900/30 text-xs"
              data-testid="bulk-remoderate-btn"
            >
              <RefreshCw className="w-3 h-3 mr-1" />
              Re-moderate Selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="text-center py-20 text-gray-400">Loading content...</div>
      ) : (
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="bg-gray-800 border border-gray-700 mb-4 md:mb-6 w-full sm:w-auto flex">
            <TabsTrigger 
              value="all" 
              className="flex-1 sm:flex-none text-xs sm:text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500"
              data-testid="all-content-tab"
            >
              <FolderOpen className="w-4 h-4 mr-1" />
              All ({content.length})
            </TabsTrigger>
            <TabsTrigger 
              value="pending"
              className="flex-1 sm:flex-none text-xs sm:text-sm data-[state=active]:bg-gradient-to-r data-[state=active]:from-yellow-400 data-[state=active]:to-purple-500"
              data-testid="pending-content-tab"
            >
              <Eye className="w-4 h-4 mr-1" />
              Pending ({pendingContent.length})
            </TabsTrigger>
          </TabsList>

          {/* Quick actions for All tab */}
          {activeTab === 'all' && content.filter(c => c.status === 'approved').length > 0 && (
            <div className="mb-4 flex gap-2">
              <Button
                onClick={selectAllApproved}
                variant="outline"
                size="sm"
                className="border-gray-600 text-gray-300 hover:bg-gray-700 text-xs"
                data-testid="select-all-approved-btn"
              >
                Select All Approved ({content.filter(c => c.status === 'approved').length})
              </Button>
            </div>
          )}

          <TabsContent value="all">
            {renderContentGrid(filteredContent, false)}
          </TabsContent>

          <TabsContent value="pending">
            {renderContentGrid(
              pendingContent.filter(item =>
                item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                item.description?.toLowerCase().includes(searchQuery.toLowerCase())
              ),
              true
            )}
          </TabsContent>
        </Tabs>
      )}

      {/* Re-moderation Dialog */}
      <Dialog open={remodDialogOpen} onOpenChange={setRemodDialogOpen}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RefreshCw className="w-5 h-5 text-purple-400" />
              Re-moderate Content
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              {remodTarget 
                ? 'Re-run moderation checks on this content item.'
                : `Re-run moderation checks on ${selectedContent.size} selected item${selectedContent.size !== 1 ? 's' : ''}.`
              }
            </DialogDescription>
          </DialogHeader>

          {!remodResults ? (
            <>
              <div className="space-y-4 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-blue-400" />
                    <Label htmlFor="ai-analysis" className="text-sm">AI Content Analysis</Label>
                  </div>
                  <Switch
                    id="ai-analysis"
                    checked={remodOptions.run_ai_analysis}
                    onCheckedChange={(checked) => setRemodOptions(prev => ({ ...prev, run_ai_analysis: checked }))}
                    data-testid="ai-analysis-switch"
                  />
                </div>
                <p className="text-xs text-gray-500 ml-6">
                  Analyze text, images, and video frames for policy violations
                </p>

                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Bug className="w-4 h-4 text-green-400" />
                    <Label htmlFor="virus-scan" className="text-sm">Virus Scan</Label>
                  </div>
                  <Switch
                    id="virus-scan"
                    checked={remodOptions.run_virus_scan}
                    onCheckedChange={(checked) => setRemodOptions(prev => ({ ...prev, run_virus_scan: checked }))}
                    data-testid="virus-scan-switch"
                  />
                </div>
                <p className="text-xs text-gray-500 ml-6">
                  Scan files for malware and viruses
                </p>
              </div>

              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setRemodDialogOpen(false)}
                  className="border-gray-600 text-gray-300"
                  data-testid="cancel-remod-btn"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleRemoderate}
                  disabled={remodLoading || (!remodOptions.run_ai_analysis && !remodOptions.run_virus_scan)}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="confirm-remod-btn"
                >
                  {remodLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Run Re-moderation
                    </>
                  )}
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              {/* Results View */}
              <div className="space-y-4 py-4">
                {remodTarget ? (
                  // Single item results
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 bg-gray-800 rounded-lg">
                      <span className="text-sm">Status</span>
                      <Badge className={`${remodResults.new_status === 'approved' ? 'bg-green-600' : 'bg-yellow-600'} text-white`}>
                        {remodResults.new_status}
                      </Badge>
                    </div>
                    
                    {remodResults.ai_analysis && (
                      <div className="p-3 bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Shield className="w-4 h-4 text-blue-400" />
                          <span className="text-sm font-medium">AI Analysis</span>
                        </div>
                        <p className="text-xs text-gray-400">
                          {remodResults.ai_analysis.passed 
                            ? 'No policy violations detected' 
                            : `Flagged: ${remodResults.ai_analysis.flagged_categories.join(', ')}`
                          }
                        </p>
                      </div>
                    )}
                    
                    {remodResults.virus_scan && (
                      <div className="p-3 bg-gray-800 rounded-lg">
                        <div className="flex items-center gap-2 mb-2">
                          <Bug className="w-4 h-4 text-green-400" />
                          <span className="text-sm font-medium">Virus Scan</span>
                        </div>
                        <p className="text-xs text-gray-400">
                          {remodResults.virus_scan.status === 'clean' 
                            ? 'No threats detected' 
                            : remodResults.virus_scan.status === 'skipped'
                            ? remodResults.virus_scan.details
                            : `Threat detected: ${remodResults.virus_scan.details}`
                          }
                        </p>
                      </div>
                    )}
                  </div>
                ) : (
                  // Bulk results
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div className="p-3 bg-gray-800 rounded-lg text-center">
                        <div className="text-2xl font-bold text-white">{remodResults.processed}</div>
                        <div className="text-xs text-gray-400">Processed</div>
                      </div>
                      <div className="p-3 bg-green-900/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-400">{remodResults.passed}</div>
                        <div className="text-xs text-gray-400">Passed</div>
                      </div>
                      <div className="p-3 bg-yellow-900/30 rounded-lg text-center">
                        <div className="text-2xl font-bold text-yellow-400">{remodResults.flagged}</div>
                        <div className="text-xs text-gray-400">Flagged</div>
                      </div>
                      <div className="p-3 bg-gray-800 rounded-lg text-center">
                        <div className="text-2xl font-bold text-gray-400">{remodResults.skipped}</div>
                        <div className="text-xs text-gray-400">Skipped</div>
                      </div>
                    </div>
                    
                    {remodResults.details && remodResults.details.length > 0 && (
                      <div className="max-h-40 overflow-y-auto">
                        {remodResults.details.filter(d => d.flagged_categories?.length > 0).map((detail, idx) => (
                          <div key={idx} className="p-2 bg-yellow-900/20 border border-yellow-700 rounded mb-2 text-xs">
                            <span className="font-medium text-yellow-400">{detail.title}</span>
                            <span className="text-gray-400"> - {detail.flagged_categories.join(', ')}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter>
                <Button
                  onClick={() => setRemodDialogOpen(false)}
                  className="bg-gray-700 hover:bg-gray-600 text-white"
                  data-testid="close-results-btn"
                >
                  Close
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManagement;
